import { createAsyncThunk } from "@reduxjs/toolkit";
import { EndPoint, ResponseCode } from "../../../common/constants";
import { showAlert } from "../../../common/constants/alert";
import {
  loginApiCall,
  postApiCallCentral,
} from "../../../common/constants/api";
import { LoginCredentials, LoginData } from "./login.type";
import { setLoading } from "../../../redux/global.slice";
import { setResetToken } from "./login.slice";
import { RootState } from "../../../redux/store";

export const handleLogin = createAsyncThunk(
  "post_api/auth_local",
  async (
    {
      credentials,
      successFn,
    }: { credentials: LoginCredentials; successFn?: (data: LoginData) => void },
    { fulfillWithValue, dispatch }
  ) => {
    dispatch(setLoading(true));
    try {
      const response = await loginApiCall(EndPoint.authLocal, credentials);

      if (response && response.data) {
        const { data, statusCode, messages } = response.data;

        if (statusCode === ResponseCode.SUCCESS && data) {
          if (data?.accessToken) {
            localStorage.setItem("accessToken", data.accessToken);
            localStorage.setItem("idToken", data.accessToken);
          }

          if (data.userId) {
            localStorage.setItem(
              "user",
              JSON.stringify({ userId: data.userId })
            );
          }

          if (credentials?.email) {
            localStorage.setItem("email", credentials.email);
          }
          localStorage.setItem(
            "forcePasswordChange",
            JSON.stringify(!!data.forcePasswordChange)
          );

          if (messages && messages.length > 0)
            showAlert(1, messages[0] || "Login successful");
          if (successFn) successFn(data);
          return fulfillWithValue(data);
        } else {
          if (messages && messages.length > 0)
            showAlert(
              2,
              messages[0] ||
                "Something went wrong during login. Please try again."
            );
        }
      }
    } catch (e) {
      showAlert(
        2,
        "Something went wrong during login. Please try again later or contact support."
      );
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const handlePasswordResetRequest = createAsyncThunk(
  "post_api/password_reset_request",
  async (
    { email, successFn }: { email: string; successFn?: (data: any) => void },
    { fulfillWithValue, dispatch }
  ) => {
    dispatch(setLoading(true));
    try {
      const response = await postApiCallCentral(
        EndPoint?.passwordResetRequest,
        { email }
      );

      if (response && response.data) {
        const { data, statusCode, messages } = response.data;

        if (
          statusCode === ResponseCode.SUCCESS ||
          response.status === 200 ||
          response.status === 201
        ) {
          if (messages && messages.length > 0)
            showAlert(1, messages[0] || "OTP sent successfully");
          if (successFn) successFn(data);
          return fulfillWithValue(data);
        } else {
          if (messages && messages.length > 0)
            showAlert(
              2,
              messages[0] || "Failed to send OTP. Please try again."
            );
        }
      }
    } catch (e) {
      showAlert(
        2,
        "Something went wrong during password reset request. Please try again later or contact support."
      );
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const handlePasswordResetValidateOtp = createAsyncThunk(
  "post_api/password_reset_validate_otp",
  async (
    {
      otpId,
      otp,
      successFn,
    }: { otpId: string; otp: string; successFn?: (data: any) => void },
    { fulfillWithValue, dispatch }
  ) => {
    dispatch(setLoading(true));
    try {
      const response = await postApiCallCentral(
        EndPoint?.passwordResetValidateOtp,
        {
          otpId,
          otp,
        }
      );

      if (response && response.data) {
        const { data, statusCode, messages } = response.data;

        if (
          statusCode === ResponseCode.SUCCESS ||
          response.status === 200 ||
          response.status === 201
        ) {
          if (messages && messages.length > 0)
            showAlert(1, messages[0] || "OTP validated successfully");
          if (successFn) successFn(data);
          console.log({ data });
          if (data?.resetToken) {
            dispatch(setResetToken(data.resetToken));
          }
          return fulfillWithValue(data);
        } else {
          if (messages && messages.length > 0)
            showAlert(2, messages[0] || "Invalid OTP. Please try again.");
        }
      }
    } catch (e) {
      showAlert(
        2,
        "Something went wrong during OTP validation. Please try again later or contact support."
      );
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const handlePasswordReset = createAsyncThunk(
  "post_api/password_reset",
  async (
    { newPassword, successFn }: { newPassword: string; successFn?: () => void },
    { fulfillWithValue, dispatch, getState }
  ) => {
    dispatch(setLoading(true));
    try {
      const resetToken = (getState() as RootState).login.resetToken;
      const response = await postApiCallCentral(EndPoint?.passwordReset, {
        resetToken,
        newPassword,
      });

      if (response && response.data) {
        const { data, statusCode, messages } = response.data;

        if (
          statusCode === ResponseCode.SUCCESS ||
          response.status === 200 ||
          response.status === 201
        ) {
          if (messages && messages.length > 0)
            showAlert(1, messages[0] || "Password reset successfully");
          if (successFn) successFn();
          return fulfillWithValue(data);
        } else {
          if (messages && messages.length > 0)
            showAlert(
              2,
              messages[0] || "Failed to reset password. Please try again."
            );
        }
      }
    } catch (e) {
      showAlert(
        2,
        "Something went wrong during password reset. Please try again later or contact support."
      );
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const handleChangePassword = createAsyncThunk(
  "post_api/change_password",
  async (
    {
      userId,
      oldPassword,
      newPassword,
      successFn,
    }: {
      userId: number;
      oldPassword: string;
      newPassword: string;
      successFn?: () => void;
    },
    { fulfillWithValue, dispatch }
  ) => {
    dispatch(setLoading(true));
    try {
      const response = await postApiCallCentral(EndPoint?.changePassword, {
        userId,
        oldPassword,
        newPassword,
      });

      if (response && response.data) {
        const { data, statusCode, messages } = response.data;

        if (
          statusCode === ResponseCode.SUCCESS ||
          response.status === 200 ||
          response.status === 201
        ) {
          if (messages && messages.length > 0)
            showAlert(1, messages[0] || "Password changed successfully");
          if (successFn) successFn();
          return fulfillWithValue(data);
        } else {
          if (messages && messages.length > 0)
            showAlert(
              2,
              messages[0] || "Failed to change password. Please try again."
            );
        }
      }
    } catch (e) {
      showAlert(
        2,
        "Something went wrong during password change. Please try again later or contact support."
      );
    } finally {
      dispatch(setLoading(false));
    }
  }
);
